import React from 'react';
import { CheckCircle, Clock, Trophy } from 'lucide-react';

// Activity Progress Bar Component
export const ActivityProgressBar = ({ activityId, progress, showDetails = false }) => {
  if (!progress) {
    return (
      <div className="w-full">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gray-300 w-0" />
        </div>
        {showDetails && (
          <p className="text-xs text-gray-400 mt-1">Non commencé</p>
        )}
      </div>
    );
  }

  const percent = progress.totalSlides > 0 
    ? Math.round((progress.viewedSlides.length / progress.totalSlides) * 100)
    : 0;
  
  const isCompleted = progress.viewedSlides.length >= progress.totalSlides && progress.quizCompleted;

  // Color based on progress
  let barColor = 'bg-gray-400'; // Not started
  if (percent > 0 && percent < 50) barColor = 'bg-amber-500';
  else if (percent >= 50 && percent < 100) barColor = 'bg-blue-500';
  else if (percent >= 100 && !progress.quizCompleted) barColor = 'bg-purple-500';
  else if (isCompleted) barColor = 'bg-green-500';

  return (
    <div className="w-full">
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {showDetails && (
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">
            {progress.viewedSlides.length}/{progress.totalSlides} slides
          </span>
          {isCompleted ? (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <CheckCircle size={12} /> Terminé
            </span>
          ) : progress.quizCompleted ? (
            <span className="text-xs text-purple-600 flex items-center gap-1">
              <Trophy size={12} /> Quiz: {progress.quizScore}/{progress.quizTotalQuestions}
            </span>
          ) : percent > 0 ? (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <Clock size={12} /> En cours
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Compact badge showing completion status
export const ProgressBadge = ({ progress }) => {
  if (!progress) {
    return null;
  }

  const percent = progress.totalSlides > 0 
    ? Math.round((progress.viewedSlides.length / progress.totalSlides) * 100)
    : 0;
  
  const isCompleted = progress.viewedSlides.length >= progress.totalSlides && progress.quizCompleted;

  if (isCompleted) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
        <CheckCircle size={12} /> Terminé
      </span>
    );
  }

  if (percent > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
        {percent}%
      </span>
    );
  }

  return null;
};

// Program overview card
export const ProgramProgressCard = ({ programName, stats }) => {
  const { totalActivities, completedActivities, progressPercent, averageQuizScore, completedQuizzes } = stats;

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <h4 className="font-semibold text-gray-800 mb-3">{programName}</h4>
      
      {/* Main progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">Progression globale</span>
          <span className="font-medium text-gray-800">{progressPercent}%</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-gray-500 text-xs">Activités</p>
          <p className="font-semibold text-gray-800">
            {completedActivities}/{totalActivities}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-gray-500 text-xs">Score moyen Quiz</p>
          <p className="font-semibold text-gray-800">
            {averageQuizScore !== null ? `${averageQuizScore}%` : '-'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivityProgressBar;
